// src/pages/harvesting/utils/excelImportHandler.js
import * as XLSX from "xlsx";
import { ref, push, update } from "firebase/database";
import {
    formatDateToDDMMYYYY,
    formatValueAsIs
} from "../../../utils/formatters-master";

/**
 * Helper untuk import data Excel ke Firebase
 * @param {File} file - File Excel yang akan diimport
 * @param {object} database - Instance Firebase database
 * @param {function} onValuePromise - Fungsi helper untuk membaca data dari Firebase
 * @returns {Promise<object>} - Hasil import { importedCount, updatedCount, ignoredCount, error }
 */
export const importExcelToFirebase = async (file, database, onValuePromise) => {
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Gunakan raw: true untuk mendapatkan nilai mentah
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true });
                
                const dataStartRow = 5;
                
                // MODIFIKASI: Tambahkan budget di kolom 12
                const columnMapping = {
                    id_lokasi: 0,
                    tanggal_closing: 3,
                    hsf: 4,
                    target_panen: 5,
                    real_panen: 6,
                    ritase: 7, 
                    ph21: 8,
                    ph22: 9,
                    pnfpg4: 10,
                    daily: 11,
                    budget: 12
                };

                console.log("Sample raw data dari Excel:");
                if (jsonData.length > dataStartRow) {
                    console.log("Row 6 raw:", jsonData[dataStartRow]);
                    console.log("hsf value type:", typeof jsonData[dataStartRow][columnMapping.hsf]);
                    console.log("hsf value:", jsonData[dataStartRow][columnMapping.hsf]);
                    console.log("budget value type:", typeof jsonData[dataStartRow][columnMapping.budget]);
                    console.log("budget value:", jsonData[dataStartRow][columnMapping.budget]);
                }

                const monitorRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
                const existingMonitorSnapshot = await onValuePromise(monitorRef);
                const existingMonitorData = existingMonitorSnapshot.val() || {};

                const existingMonitorMap = new Map();
                Object.keys(existingMonitorData).forEach(fbKey => {
                    const record = existingMonitorData[fbKey];
                    if (record.id_lokasi) {
                        existingMonitorMap.set(record.id_lokasi.toString().trim(), fbKey);
                    }
                });

                const importTimestamp = new Date().toISOString();

                let importedCount = 0;
                let updatedCount = 0;
                let ignoredCount = 0;

                // Proses data
                for (let i = dataStartRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const id_lokasi = row[columnMapping.id_lokasi];
                    const tanggal_closing = row[columnMapping.tanggal_closing];
                    const hsf = row[columnMapping.hsf];
                    const target_panen = row[columnMapping.target_panen];
                    const real_panen = row[columnMapping.real_panen];
                    const ritase = row[columnMapping.ritase];
                    const ph21 = row[columnMapping.ph21];
                    const ph22 = row[columnMapping.ph22];
                    const pnfpg4 = row[columnMapping.pnfpg4];
                    const daily = row[columnMapping.daily];
                    const budget = row[columnMapping.budget]; // Ambil nilai budget
                    
                    if (!id_lokasi || (typeof id_lokasi === 'string' && id_lokasi.trim() === '')) {
                        ignoredCount++;
                        continue;
                    }

                    const formattedIdLokasi = String(id_lokasi).trim();
                    
                    // Debug: lihat nilai asli sebelum diformat
                    if (importedCount + updatedCount < 2) {
                        console.log(`Data baris ${i + 1} sebelum format:`);
                        console.log(`  hsf raw:`, hsf, `type:`, typeof hsf);
                        console.log(`  target_panen raw:`, target_panen, `type:`, typeof target_panen);
                        console.log(`  budget raw:`, budget, `type:`, typeof budget);
                    }

                    // HANYA data dari Excel yang akan disimpan di Firebase
                    const excelDataToUpdate = {
                        tanggal_closing: formatDateToDDMMYYYY(tanggal_closing),
                        hsf: formatValueAsIs(hsf),
                        target_panen: formatValueAsIs(target_panen),
                        real_panen: formatValueAsIs(real_panen),
                        ritase: formatValueAsIs(ritase),
                        ph21: formatValueAsIs(ph21),
                        ph22: formatValueAsIs(ph22),
                        pnfpg4: formatValueAsIs(pnfpg4),
                        daily: formatValueAsIs(daily),
                        budget: formatValueAsIs(budget), // Tambahkan budget ke data yang akan disimpan
                        last_imported_at: importTimestamp,
                    };

                    const firebaseKey = existingMonitorMap.get(formattedIdLokasi);

                    try {
                        if (firebaseKey) {
                            // Jika data sudah ada, hanya update kolom dari Excel
                            // TIDAK menyentuh kolom lain yang mungkin sudah ada
                            await update(ref(database, `db_harvesting/tb_monitor_hasil_panen/${firebaseKey}`), excelDataToUpdate);
                            updatedCount++;
                        } else {
                            // Jika data baru, buat HANYA dengan data dari Excel + id_lokasi
                            const newData = {
                                id_lokasi: formattedIdLokasi,
                                // Hanya data dari Excel yang disimpan
                                ...excelDataToUpdate
                                // TIDAK menyertakan: status, lokasi, tahun, luas, dll
                                // Kolom tersebut akan diambil dari sumber lain
                            };
                            await push(monitorRef, newData);
                            importedCount++;
                        }
                        
                        // Debug setelah save pertama
                        if (importedCount + updatedCount === 1) {
                            console.log("Data pertama yang disimpan:", firebaseKey ? excelDataToUpdate : "new data");
                        }
                    } catch (error) {
                        console.error(`Error baris ${i + 1}:`, error);
                        ignoredCount++;
                    }
                }

                console.log(`Hasil: ${importedCount} baru, ${updatedCount} update, ${ignoredCount} abaikan`);

                resolve({
                    success: true,
                    importedCount,
                    updatedCount,
                    ignoredCount,
                    total: importedCount + updatedCount
                });

            } catch (error) {
                console.error("Gagal impor Excel:", error);
                reject({
                    success: false,
                    error: "Terjadi kesalahan saat mengimpor data dari Excel.",
                    details: error.message
                });
            }
        };

        reader.onerror = () => {
            reject({
                success: false,
                error: "Gagal membaca file."
            });
        };

        reader.readAsArrayBuffer(file);
    });
};

/**
 * Fungsi untuk refresh data setelah import
 * @param {object} database - Instance Firebase database
 * @param {function} onValuePromise - Fungsi helper untuk membaca data dari Firebase
 * @returns {Promise<array>} - Array data yang sudah di-refresh
 */
export const refreshMonitorData = async (database, onValuePromise) => {
    const monitorRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
    const updatedMonitorSnapshot = await onValuePromise(monitorRef);
    const updatedMonitorData = updatedMonitorSnapshot.val() || {};
    
    const updatedLoadedData = [];
    Object.keys(updatedMonitorData).forEach(key => {
        const record = updatedMonitorData[key];
        // Hanya ambil data yang disimpan di Firebase
        updatedLoadedData.push({ 
            firebaseKey: key, 
            // Hanya data dari Excel yang disimpan di Firebase
            id_lokasi: record.id_lokasi || '',
            tanggal_closing: record.tanggal_closing || '-',
            hsf: record.hsf || '-',
            target_panen: record.target_panen || '-',
            real_panen: record.real_panen || '-',
            ritase: record.ritase || '-',
            ph21: record.ph21 || '-',
            ph22: record.ph22 || '-',
            pnfpg4: record.pnfpg4 || '-',
            daily: record.daily || '-',
            budget: record.budget || '-', // Tambahkan budget
            // Kolom lainnya diisi dengan default value
            // Akan diupdate dengan data dari sumber lain di komponen
            status: '',
            lokasi: record.id_lokasi || '',
            tahun: '',
            luas: '-',
            jenis_bibit: '-',
            kepala_kebun: '-',
            kepala_wilayah: '-',
            pencapaian_panen: '-',
            luas_terpanen: '-',
            yield: '-',
            var_yield_renc_vs_real: '-',
            rencana_yield: '-',
            grade_tanpa_crown: '-',
            selisih: '-'
        });
    });
    
    return updatedLoadedData;
};